import { Children, cloneElement, isValidElement } from 'react';
import type { ComponentProps, ReactNode } from 'react';

import type { MenuItemSize } from '@playbook/atoms/menu-item';
import { cn } from '@playbook/utils';

import css from './styles.module.css';

export type MenuVariant = 'menu' | 'sheet' | 'inline';

const DefaultMenuVariant: MenuVariant = 'menu' as const;

export type MenuSize = MenuItemSize;

const DefaultMenuSize: MenuSize = 'md' as const;

/**
 *	`group` is a list of actions, reached with Tab — see the note on the
 *	component. `listbox` is a list of things to pick from while focus stays
 *	somewhere else, which is what a field's suggestions are: the parent that
 *	claims it also puts `option` on every row and moves an active index through
 *	them. See `compositions/mention-list`.
 */
export type MenuRole = 'group' | 'listbox';

const DefaultMenuRole: MenuRole = 'group' as const;

/** The one prop the menu owns and overwrites on every item. */
interface MenuOptionProperties {
	size?: MenuItemSize;
}

type MenuEventHandlers = {
	[K in keyof ComponentProps<'div'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'div'>[K];
};

interface MenuDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface MenuProperties extends MenuEventHandlers, MenuDataAttributes {
	variant?: MenuVariant;
	size?: MenuSize;
	role?: MenuRole;
	/**
	 *	For a `listbox`, which a field points at through `aria-controls` and
	 *	so needs to be findable by id. Not a hook for styling.
	 */
	id?: string;

	/** Names the menu for assistive technology, which sees no visible label. */
	label: string;

	children: ReactNode;
}

/**
 *	A list of choices on its own surface.
 *
 *	Scale belongs to the menu, not to the items in it: rows of two heights read
 *	as two lists that happen to touch. So the menu writes `size` onto every
 *	item, and warns rather than silently winning when a caller set it.
 *
 *	It is inert — it does not open, close or position itself. Whatever shows it
 *	owns that, which is what lets the same menu be a popover in one place and a
 *	sheet docked to the bottom of a phone screen in another.
 *
 *	`role="group"`, not `role="menu"`. The ARIA menu pattern promises arrow keys
 *	move between items and Tab leaves — and nothing here implements that, so the
 *	items are ordinary buttons reached with Tab and are announced as such.
 *	Claiming the richer role would only mislead whoever relies on it.
 *
 *	`listbox` is the one other role it takes, and it makes the same promise the
 *	same way: the menu still implements no keys. Whoever claims it owns the
 *	arrow keys, in the field the list belongs to.
 */
export function Menu({
	variant = DefaultMenuVariant,
	size = DefaultMenuSize,
	role = DefaultMenuRole,
	id,

	label,

	children,

	...props
}: MenuProperties): ReactNode {
	const options: ReactNode = Children.map(children, (child) => {
		if (!isValidElement<MenuOptionProperties>(child)) {
			return child;
		}

		if (child.props.size !== undefined) {
			console.warn(
				'Menu sets `size` for every item. Set it on the menu; the ones on its children are ignored.'
			);
		}

		return cloneElement(child, { size });
	});

	/**
	 *	The base class is `surface`, not `menu`: one value of the variant axis is
	 *	also called `menu`, and naming both the same would put every variant rule
	 *	on the base class, where it would reach `sheet` too.
	 */
	const className: string = cn(
		css.surface,
		css[variant],
		size === 'sm' && css.sm
	);

	return (
		<div
			{...props}
			role={role}
			id={id}
			aria-label={label}
			data-variant={variant}
			data-size={size}
			className={className}>
			{options}
		</div>
	);
}
